import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { useChatStore } from '../../../../../store/chatStore';
import type { GenericItem } from '../types';
import {
  ChatBubbleLeftEllipsisIcon,
  SparklesIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/outline';
import { AssetIcon, Tooltip } from '@plerionhq/design-system';
import { ArrowRightIcon } from '@heroicons/react/24/solid';

import { useTranslation } from 'react-i18next';

export interface MoreOption {
  label: React.ReactNode;
  message: string;
  tooltip?: string;
}

interface MoreOptionsMenuOption {
  label: React.ReactNode;
  onClick: () => void;
  tooltip?: string;
}

interface MoreOptionsMenuProps {
  options: MoreOptionsMenuOption[];
  side?: 'left' | 'right';
  visibilityClass?: string;
  size?: 'default' | 'compact';
  buttonToneClass?: string;
}

const MoreOptionsMenu: React.FC<MoreOptionsMenuProps> = ({
  options,
  side = 'left',
  visibilityClass = 'invisible group-focus-within:visible group-hover:visible',
  size = 'default',
  buttonToneClass,
}) => {
  const { t } = useTranslation('pleri/chat');
  const [open, setOpen] = useState(false);
  const buttonSizeClass = size === 'compact' ? 'p-[5px]' : 'p-2.5';
  const iconSizeClass = size === 'compact' ? 'h-3 w-3' : 'h-5 w-5';
  const resolvedButtonToneClass =
    buttonToneClass ||
    'bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 ring-gray-200 dark:ring-gray-700 hover:bg-blue-50 hover:text-blue-600 hover:ring-blue-200';

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Tooltip label={t('moreActions')} placement={side}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={`${visibilityClass} ${buttonSizeClass} ${resolvedButtonToneClass} rounded-full shadow-md ring-1 transition-colors`}
            aria-label={t('moreActions')}
          >
            <EllipsisVerticalIcon className={iconSizeClass} />
          </button>
        </Popover.Trigger>
      </Tooltip>
      <Popover.Portal>
        <Popover.Content
          id="search-app"
          side={side}
          align="center"
          sideOffset={8}
          style={{ zIndex: 99999999999999 }}
        >
          <div className="z-[99999999999999] w-[200px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-900">
            {options.map((opt, i) => {
              const button = (
                <button
                  type="button"
                  onClick={() => {
                    opt.onClick();
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                >
                  {opt.label}
                </button>
              );
              return opt.tooltip ? (
                <Tooltip key={i} label={opt.tooltip} placement="left">
                  {button}
                </Tooltip>
              ) : (
                <React.Fragment key={i}>{button}</React.Fragment>
              );
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export interface RemediationConfig {
  /** Tooltip label on the button */
  tooltip: string;
  /** User-visible message sent to the AI */
  message: string;
  /** Icon to render — defaults to SparklesIcon when not provided */
  icon?: React.ComponentType<{ className?: string }>;
  /** Tailwind hover colour classes — defaults to green (suggest-fix style) */
  hoverClasses?: string;
}

interface PleriActionCellProps {
  row: {
    original: GenericItem;
  };
  showRemediationButton?: boolean;
  /** When provided, overrides the default "Suggest fix" label / prompt / icon. */
  remediationConfig?: RemediationConfig;
  moreOptions?: MoreOption[];
  position?: 'left' | 'right';
  hoverScope?: 'default' | 'asset-card';
  alwaysVisible?: boolean;
  size?: 'default' | 'compact';
  direction?: 'vertical' | 'horizontal';
  /** When set, replaces the "Ask Pleri" free-text input with a direct-action
   *  button that sends `message` on click (rendered with AssetIcon). */
  primaryAction?: {
    label: string;
    message: string;
  };
}

export const PleriActionCell: React.FC<PleriActionCellProps> = ({
  row,
  showRemediationButton,
  remediationConfig,
  moreOptions,
  position = 'left',
  hoverScope = 'default',
  alwaysVisible = false,
  size = 'default',
  direction = 'vertical',
  primaryAction,
}) => {
  const { t } = useTranslation('pleri/chat');
  const [showInput, setShowInput] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLDivElement>(null);

  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );
  const addMessage = useChatStore((state) => state.addMessage);
  const createConversation = useChatStore((state) => state.createConversation);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowInput(false);
        setInputValue('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = (text: string) => {
    if (!text.trim()) {
      return;
    }

    const rowData = JSON.stringify(row.original, null, 2);
    const messageText = `<usermessage>${text}</usermessage>\n\n<context>User has provided the following context:\`\`\`json\n${rowData}\`\`\`</context>`;

    if (!activeConversationId) {
      const newId = createConversation();
      addMessage(newId, {
        content: [{ type: 'text', text: messageText }],
        role: 'user',
      });
    } else {
      addMessage(activeConversationId, {
        content: [{ type: 'text', text: messageText }],
        role: 'user',
      });
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(inputValue);
      setInputValue('');
      setShowInput(false);
    } else if (e.key === 'Escape') {
      setShowInput(false);
      setInputValue('');
    }
  };

  const handleSuggestFix = () => {
    const userMessage =
      remediationConfig?.message ?? 'Suggest a fix for this item.';
    const rowData = JSON.stringify(row.original, null, 2);
    const messageText = `<usermessage>${userMessage}</usermessage>\n\n<context>User has provided the following context:\`\`\`json\n${rowData}\`\`\`</context>`;

    if (!activeConversationId) {
      const newId = createConversation();
      addMessage(newId, {
        content: [{ type: 'text', text: messageText }],
        role: 'user',
      });
    } else {
      addMessage(activeConversationId, {
        content: [{ type: 'text', text: messageText }],
        role: 'user',
      });
    }
  };

  const attachedContextJson = JSON.stringify(row.original, null, 2);
  const sendMessage = (userMessage: string) => {
    const messageText = `<usermessage>${userMessage}</usermessage>\n\n<context>User has provided the following context:\`\`\`json\n${attachedContextJson}\`\`\`</context>`;

    if (!activeConversationId) {
      const newId = createConversation();
      addMessage(newId, {
        content: [{ type: 'text', text: messageText }],
        role: 'user',
      });
    } else {
      addMessage(activeConversationId, {
        content: [{ type: 'text', text: messageText }],
        role: 'user',
      });
    }
  };

  const side = position === 'right' ? 'right' : 'left';
  const actionContainerClass =
    side === 'right'
      ? direction === 'horizontal'
        ? 'absolute right-3 top-3 z-[100] flex flex-row gap-1.5'
        : 'absolute right-3 top-3 z-[100] flex flex-col gap-2'
      : direction === 'horizontal'
        ? 'absolute left-3 top-3 z-[100] flex flex-row gap-1.5'
        : 'absolute -left-4 top-1/2 z-[100] flex -translate-y-1/2 transform flex-col space-y-2';
  const inputAnchorClass =
    side === 'right'
      ? 'absolute right-3 top-3 z-[200]'
      : 'absolute left-0 top-1/2 z-[200] -translate-y-1/2 transform';
  const inputBoxClass =
    side === 'right'
      ? 'relative mr-12 flex w-[300px] items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2 shadow-lg'
      : 'relative ml-12 flex w-[300px] items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-2 shadow-lg';
  const buttonSizeClass = size === 'compact' ? 'p-[5px]' : 'p-2.5';
  const iconSizeClass = size === 'compact' ? 'h-3 w-3' : 'h-5 w-5';
  const visibilityClass = alwaysVisible
    ? 'visible'
    : hoverScope === 'asset-card'
      ? 'invisible group-focus-within/asset-card:visible group-hover/asset-card:visible'
      : 'invisible group-focus-within:visible group-hover:visible';
  const bubbleToneClass =
    hoverScope === 'asset-card'
      ? 'bg-orange-50 dark:bg-gray-700 text-orange-600 dark:text-gray-400 ring-orange-200 dark:ring-gray-600 hover:bg-orange-100 dark:hover:bg-gray-600 hover:text-orange-700 dark:hover:text-orange-400 hover:ring-orange-300 dark:hover:ring-gray-500'
      : 'bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 ring-gray-200 dark:ring-gray-700 hover:bg-orange-50 hover:text-orange-600 hover:ring-orange-200';
  const bubbleButtonClass = `${visibilityClass} ${buttonSizeClass} ${bubbleToneClass} rounded-full shadow-md ring-1 transition-colors`;

  if (!primaryAction && showInput) {
    return (
      <div ref={inputRef} className={inputAnchorClass}>
        <div className={inputBoxClass}>
          <ChatBubbleLeftEllipsisIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('askAboutThisItem')}
            className="flex-1 border-none bg-transparent text-sm outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500"
            autoFocus
            style={{
              boxShadow: 'none',
            }}
          />
          <button
            onClick={() => {
              handleSubmit(inputValue);
              setInputValue('');
              setShowInput(false);
            }}
            disabled={!inputValue.trim()}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-[#FF732E] text-white transition-colors hover:bg-[#E65A1F] disabled:bg-gray-300"
          >
            <ArrowRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={actionContainerClass}>
      {primaryAction ? (
        <Tooltip label={primaryAction.label} placement={side}>
          <button
            onClick={() => sendMessage(primaryAction.message)}
            className={bubbleButtonClass}
          >
            <AssetIcon className={iconSizeClass} />
          </button>
        </Tooltip>
      ) : (
        <Tooltip label={t('askPleri')} placement={side}>
          <button
            onClick={() => setShowInput(true)}
            className={bubbleButtonClass}
          >
            <ChatBubbleLeftEllipsisIcon className={iconSizeClass} />
          </button>
        </Tooltip>
      )}
      {showRemediationButton &&
        (() => {
          const RemediationIcon = remediationConfig?.icon ?? SparklesIcon;
          const remediationTooltip =
            remediationConfig?.tooltip ?? t('suggestFix');
          const remediationHover =
            remediationConfig?.hoverClasses ??
            'hover:bg-green-50 hover:text-green-600 hover:ring-green-200';
          return (
            <Tooltip label={remediationTooltip} placement={side}>
              <button
                onClick={handleSuggestFix}
                className={`${visibilityClass} ${buttonSizeClass} rounded-full bg-orange-50 dark:bg-gray-700 text-orange-600 dark:text-gray-400 shadow-md ring-1 ring-orange-200 dark:ring-gray-600 transition-colors ${remediationHover}`}
              >
                <RemediationIcon className={iconSizeClass} />
              </button>
            </Tooltip>
          );
        })()}
      {moreOptions?.length === 1 && (
        <Tooltip
          label={
            moreOptions[0].tooltip ??
            (typeof moreOptions[0].label === 'string'
              ? moreOptions[0].label
              : t('more'))
          }
          placement={side}
        >
          <button
            type="button"
            onClick={() => sendMessage(moreOptions[0].message)}
            className={bubbleButtonClass}
          >
            {typeof moreOptions[0].label === 'string' ? (
              <span className="text-sm font-medium">
                {moreOptions[0].label}
              </span>
            ) : (
              <>{moreOptions[0].label}</>
            )}
          </button>
        </Tooltip>
      )}
      {moreOptions && moreOptions.length > 1 && (
        <MoreOptionsMenu
          side={side}
          visibilityClass={visibilityClass}
          size={size}
          buttonToneClass={bubbleToneClass}
          options={moreOptions.map((opt) => ({
            label:
              typeof opt.label === 'string' ? (
                <span className="font-medium">{opt.label}</span>
              ) : (
                opt.label
              ),
            onClick: () => sendMessage(opt.message),
            tooltip: opt.tooltip,
          }))}
        />
      )}
    </div>
  );
};
